import React, { useRef, useState, useCallback } from 'react';
import { useMediaUpload, type UploadedMedia } from '../hooks/useMediaUpload';

export interface AssetEntry {
  mediaId: string | null;
  url: string;
  type: 'IMAGE' | 'VIDEO';
  position: number;
  productId?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  _previewUrl?: string;
}

interface MediaUploaderProps {
  assets: AssetEntry[];
  onChange: (assets: AssetEntry[]) => void;
  maxAssets?: number;
  solutionId?: string;
  showPerAssetFields?: boolean;
}

export function MediaUploader({
  assets,
  onChange,
  maxAssets = 1,
  solutionId = 'rack-and-box',
  showPerAssetFields = false,
}: MediaUploaderProps) {
  const { upload, isUploading } = useMediaUpload();
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;
      const remaining = maxAssets - assets.length;
      const toProcess = Array.from(files).slice(0, remaining);
      let current = [...assets];

      for (const file of toProcess) {
        const previewUrl = URL.createObjectURL(file);
        const isVideo = file.type.startsWith('video/');

        const placeholder: AssetEntry = {
          mediaId: null,
          url: previewUrl,
          type: isVideo ? 'VIDEO' : 'IMAGE',
          position: current.length,
          _previewUrl: previewUrl,
        };
        current = [...current, placeholder];
        onChange(current);

        try {
          const uploaded: UploadedMedia = await upload(file, solutionId);
          current = current.map((a) =>
            a._previewUrl === previewUrl
              ? { ...a, mediaId: uploaded.id, url: uploaded.url, _previewUrl: undefined }
              : a,
          );
          onChange(current);
        } catch {
          current = current.filter((a) => a._previewUrl !== previewUrl);
          onChange(current);
        }
      }
    },
    [assets, maxAssets, onChange, upload, solutionId],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const removeAsset = (index: number) => {
    const updated = assets.filter((_, i) => i !== index).map((a, i) => ({ ...a, position: i }));
    onChange(updated);
  };

  const updateAsset = (index: number, patch: Partial<AssetEntry>) => {
    onChange(assets.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  };

  const canAdd = assets.length < maxAssets;

  return (
    <div className="space-y-3">
      {/* Upload zone */}
      {canAdd && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-[12px] p-6 cursor-pointer text-center transition-colors ${
            dragging ? 'border-[#3F3E42] bg-muted' : 'border-[#D9D9D9] hover:border-[#9B97A1]'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            multiple={maxAssets > 1}
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          {isUploading ? (
            <p className="text-[13px] text-muted-foreground">Uploading…</p>
          ) : (
            <>
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted mx-auto mb-2">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="#7C797C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="17 8 12 3 7 8" stroke="#7C797C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="12" y1="3" x2="12" y2="15" stroke="#7C797C" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <p className="text-[13px] text-muted-foreground">
                <span className="font-[600] text-foreground">Click to upload</span> or drag and drop
              </p>
              <p className="text-[11px] text-muted-foreground mt-1">Images or videos · max {maxAssets} asset{maxAssets > 1 ? 's' : ''}</p>
            </>
          )}
        </div>
      )}

      {/* Asset list */}
      {assets.length > 0 && (
        <div className="space-y-3">
          {assets.map((asset, i) => (
            <div key={i} className="border border-[#E5E7EB] rounded-[10px] p-3 bg-card">
              <div className="flex items-start gap-3">
                {/* Preview */}
                <div className="w-16 h-16 rounded-[8px] overflow-hidden bg-muted flex-shrink-0">
                  {asset.type === 'VIDEO' ? (
                    <video src={asset._previewUrl ?? asset.url} className="w-full h-full object-cover" />
                  ) : (
                    <img src={asset._previewUrl ?? asset.url} alt="" className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] text-muted-foreground truncate">{asset.url}</p>
                  {asset._previewUrl && <p className="text-[11px] text-amber-600 mt-0.5">Uploading…</p>}
                </div>
                <button
                  onClick={() => removeAsset(i)}
                  className="p-1 rounded-[6px] hover:bg-[#FECDCD] text-muted-foreground hover:text-red-500 transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4h12M5.333 4V2.667h5.334V4M6.667 7.333v4M9.333 7.333v4M3.333 4l.667 9.333h8L12.667 4" stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>

              {/* Per-asset fields */}
              {showPerAssetFields && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-[11px] font-[500] text-muted-foreground block mb-1">Product ID</label>
                    <input
                      type="text"
                      value={asset.productId ?? ''}
                      onChange={(e) => updateAsset(i, { productId: e.target.value || undefined })}
                      placeholder="Optional"
                      className="w-full border border-[#D9D9D9] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:border-[#3F3E42]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-[500] text-muted-foreground block mb-1">CTA Label</label>
                    <input
                      type="text"
                      value={asset.ctaLabel ?? ''}
                      onChange={(e) => updateAsset(i, { ctaLabel: e.target.value || undefined })}
                      placeholder="e.g. Shop Now"
                      className="w-full border border-[#D9D9D9] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:border-[#3F3E42]"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-[500] text-muted-foreground block mb-1">CTA URL</label>
                    <input
                      type="text"
                      value={asset.ctaUrl ?? ''}
                      onChange={(e) => updateAsset(i, { ctaUrl: e.target.value || undefined })}
                      placeholder="/collections/new"
                      className="w-full border border-[#D9D9D9] rounded-[6px] px-2 py-1 text-[12px] focus:outline-none focus:border-[#3F3E42]"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
